import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';

const AdminNoticeForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { notices, addNotice, updateNotice, adminUser } = useAdmin();
  const isEditing = id && id !== 'new';
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    isImportant: false,
    author: adminUser?.name || 'Admin'
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  // Load existing data in edit mode
  useEffect(() => {
    if (isEditing) {
      const notice = notices.find(n => n.id === id);
      if (notice) {
        setFormData({
          title: notice.title,
          content: notice.content,
          isImportant: notice.isImportant,
          author: notice.author
        });
      } else {
        navigate('/admin/notices');
      }
    }
  }, [id, isEditing, notices, navigate]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Please enter a title.';
    } else if (formData.title.length > 200) {
      newErrors.title = 'Title must be within 200 characters.';
    }

    if (!formData.content.trim()) {
      newErrors.content = 'Please enter content.';
    } else if (formData.content.length < 10) {
      newErrors.content = 'Content must be at least 10 characters.';
    }

    if (!formData.author.trim()) {
      newErrors.author = 'Please enter an author.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof typeof formData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear field error on input
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing && id) {
        updateNotice(id, {
          title: formData.title.trim(),
          content: formData.content.trim(),
          isImportant: formData.isImportant,
          author: formData.author.trim(),
          updatedAt: new Date().toISOString()
        });
      } else {
        addNotice({
          id: `notice_${Date.now()}`,
          title: formData.title.trim(),
          content: formData.content.trim(),
          isImportant: formData.isImportant,
          author: formData.author.trim(),
          views: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
      }
      
      navigate('/admin/notices');
    } catch (error) {
      console.error('Error saving notice:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDraft = () => {
    if (!formData.title.trim() && !formData.content.trim()) {
      alert('Please enter a title or content.');
      return;
    }

    // Save draft to localStorage
    const draftKey = `admin_notice_draft_${id || 'new'}`;
    localStorage.setItem(draftKey, JSON.stringify(formData));
    alert('Draft saved.');
  };

  const loadDraft = () => {
    const draftKey = `admin_notice_draft_${id || 'new'}`;
    const saved = localStorage.getItem(draftKey);
    if (saved) {
      const draftData = JSON.parse(saved);
      setFormData(draftData);
      localStorage.removeItem(draftKey);
      alert('Draft has been loaded.');
    } else {
      alert('No saved draft found.');
    }
  };

  const formatContentForPreview = (content: string) => {
    return content
      .split('\n')
      .map(line => line.trim())
      .filter(line => line.length > 0)
      .map((line, index) => (
        <p key={index} className="mb-3">
          {line}
        </p>
      ));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link to="/admin/notices" className="text-neutral-400 hover:text-neutral-500">
                  Notice Management
                </Link>
              </li>
              <li>
                <svg className="flex-shrink-0 h-5 w-5 text-neutral-300" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                </svg>
              </li>
              <li>
                <span className="text-neutral-500">
                  {isEditing ? 'Edit Notice' : 'New Notice'}
                </span>
              </li>
            </ol>
          </nav>
          <h1 className="mt-2 text-2xl font-bold text-neutral-900">
            {isEditing ? 'Edit Notice' : 'New Notice'}
          </h1>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            type="button"
            onClick={loadDraft}
            className="inline-flex items-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
          >
            Load Draft
          </button>
          <button
            type="button"
            onClick={() => setPreviewMode(!previewMode)}
            className="inline-flex items-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
          >
            {previewMode ? 'Edit Mode' : 'Preview'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Form */}
        <div className="lg:col-span-2">
          {previewMode ? (
            /* Preview Mode */
            <div className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-neutral-200">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-medium text-neutral-900">Preview</h2>
                  {formData.isImportant && (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                      Important Notice
                    </span>
                  )}
                </div>
              </div>
              <div className="p-6">
                <h1 className="text-2xl font-bold text-neutral-900 mb-4">{formData.title || 'Title'}</h1>
                <div className="text-sm text-neutral-500 mb-6">
                  Author: {formData.author} | Created: {new Date().toLocaleDateString('en-US')}
                </div>
                <div className="prose max-w-none">
                  {formatContentForPreview(formData.content || 'Please enter content.')}
                </div>
              </div>
            </div>
          ) : (
            /* Edit Mode */
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="bg-white shadow rounded-lg">
                <div className="px-6 py-4 border-b border-neutral-200">
                  <h2 className="text-lg font-medium text-neutral-900">Notice Information</h2>
                </div>
                <div className="p-6 space-y-6">
                  {/* Title */}
                  <div>
                    <label htmlFor="title" className="block text-sm font-medium text-neutral-700">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="title"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                        errors.title ? 'border-red-300' : 'border-neutral-300'
                      }`}
                      placeholder="Enter notice title"
                      maxLength={200}
                    />
                    {errors.title && (
                      <p className="mt-1 text-sm text-red-600">{errors.title}</p>
                    )}
                    <p className="mt-1 text-sm text-neutral-500">
                      {formData.title.length}/200characters
                    </p>
                  </div>

                  {/* Author */}
                  <div>
                    <label htmlFor="author" className="block text-sm font-medium text-neutral-700">
                      Author <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="author"
                      value={formData.author}
                      onChange={(e) => handleInputChange('author', e.target.value)}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                        errors.author ? 'border-red-300' : 'border-neutral-300'
                      }`}
                      placeholder="Enter author name"
                    />
                    {errors.author && (
                      <p className="mt-1 text-sm text-red-600">{errors.author}</p>
                    )}
                  </div>

                  {/* Important Flag */}
                  <div className="flex items-center">
                    <input
                      id="isImportant"
                      type="checkbox"
                      checked={formData.isImportant}
                      onChange={(e) => handleInputChange('isImportant', e.target.checked)}
                      className="h-4 w-4 text-forest-400 focus:ring-forest-500 border-neutral-300 rounded"
                    />
                    <label htmlFor="isImportant" className="ml-2 block text-sm text-neutral-900">
                      Mark as Important Notice
                    </label>
                  </div>

                  {/* Content */}
                  <div>
                    <label htmlFor="content" className="block text-sm font-medium text-neutral-700">
                      Content <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      id="content"
                      rows={15}
                      value={formData.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-forest-500 focus:border-forest-500 ${
                        errors.content ? 'border-red-300' : 'border-neutral-300'
                      }`}
                      placeholder="Enter notice content&#10;&#10;Use line breaks to separate paragraphs."
                    />
                    {errors.content && (
                      <p className="mt-1 text-sm text-red-600">{errors.content}</p>
                    )}
                    <p className="mt-1 text-sm text-neutral-500">
                      {formData.content.length}characters (Minimum 10characters or more)
                    </p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between">
                <div className="flex space-x-3">
                  <Link
                    to="/admin/notices"
                    className="inline-flex items-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
                  >
                    Cancel
                  </Link>
                  <button
                    type="button"
                    onClick={handleDraft}
                    className="inline-flex items-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500"
                  >
                    Save Draft
                  </button>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-forest-400 hover:bg-forest-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-forest-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Saving...
                    </>
                  ) : (
                    isEditing ? 'Save Changes' : 'Submit'
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Writing Guide */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-medium text-neutral-900">Writing Guide</h3>
            </div>
            <div className="p-6">
              <div className="space-y-4 text-sm text-neutral-600">
                <div>
                  <h4 className="font-medium text-neutral-900">Title Writing Tips</h4>
                  <ul className="mt-2 space-y-1">
                    <li>• Use clear and specific titles</li>
                    <li>• Keep it concise, within 200 characters</li>
                    <li>• Place important keywords at the beginning</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900">Content Writing Tips</h4>
                  <ul className="mt-2 space-y-1">
                    <li>• Use clear and easy-to-understand sentences</li>
                    <li>• Place important information at the top</li>
                    <li>• Improve readability with paragraph breaks</li>
                    <li>• Verify accuracy when including contact info</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-neutral-900">Important Notice Setting</h4>
                  <p className="mt-2">
                    Marking as important will pin the notice to the top of the list
                    and add a red badge.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white shadow rounded-lg">
            <div className="px-6 py-4 border-b border-neutral-200">
              <h3 className="text-lg font-medium text-neutral-900">Quick Actions</h3>
            </div>
            <div className="p-6 space-y-3">
              <Link
                to="/admin/notices"
                className="block w-full text-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
              >
                Go to Notice List
              </Link>
              {isEditing && (
                <Link
                  to="/admin/notices/new"
                  className="block w-full text-center px-4 py-2 border border-neutral-300 shadow-sm text-sm font-medium rounded-md text-neutral-700 bg-white hover:bg-neutral-50"
                >
                  New Notice
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNoticeForm;